import React, {useEffect, useState} from 'react';
import {Alert, ActivityIndicator, Modal, TouchableWithoutFeedback, View} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useTheme} from 'styled-components/native';
import remoteConfig from '@react-native-firebase/remote-config';
import analytics from '@react-native-firebase/analytics';

import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {enviarPropostaCredito, simularCredito} from '../../../services/creditoMova';

import {
  Banner,
  BannerSubtitle,
  BannerTitle,
  CloseButton,
  CloseIcon,
  Container,
  Content,
  DayOptionsRow,
  DayPill,
  DayPillText,
  DisclaimerText,
  Divider,
  Footer,
  IconCheck,
  IconWrapper,
  ModalCard,
  ModalOverlay,
  ModalTitle,
  PhoneModalBody,
  PhoneModalButtonWrapper,
  PhoneModalCard,
  PhoneModalDivider,
  PhoneModalIcon,
  PhoneModalIconWrapper,
  PhoneModalOverlay,
  PhoneModalPhoneNumber,
  PhoneModalPhoneRow,
  PhoneModalTitle,
  PhoneModalWhatsIcon,
  StepIcon,
  StepRow,
  StepText,
  StepsContainer,
  StepsTitle,
  SummaryCard,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
} from './styles';

import {RFValue} from 'react-native-responsive-fontsize';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';

type RouteParams = {
  simulacao: any;
  installments_option_id: string;
  selectedOption: any;
  endereco_id: string;
  domicilio_id: string;
};

function formatValor(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) {return '';}
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export function ConfirmacaoProposta() {
  const navigation = useNavigation();
  const route = useRoute();
  const theme = useTheme();
  const params = route.params as RouteParams;

  const [loading, setLoading] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [resimulating, setResimulating] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [numeroSuporte1, setNumeroSuporte1] = useState('');
  const [numeroSuporte2, setNumeroSuporte2] = useState('');
  const MOSTRAR_DOIS_NUMEROS = false;

  const {simulacao, installments_option_id, selectedOption} = params;
  const operacaoId = simulacao?.operacao_id;

  const [currentOption, setCurrentOption] = useState(selectedOption);
  const [currentInstallmentsOptionId, setCurrentInstallmentsOptionId] = useState(installments_option_id);
  const [currentOperacaoId, setCurrentOperacaoId] = useState(operacaoId);

  const MOVA_PRODUCT_ID = 1406;

  useEffect(() => {
    remoteConfig()
      .fetchAndActivate()
      .then(() => {
        setNumeroSuporte1(remoteConfig().getValue('Numero_Suporte_Multa_1').asString());
        setNumeroSuporte2(remoteConfig().getValue('Numero_Suporte_Multa_2').asString());
      })
      .catch(() => {});
  }, []);

useEffect(() => {
  analytics().logEvent('emprestimo_tela_confirmacao_proposta');
}, []);

  const handleSelectDay = async (day: number) => {
    setShowDateModal(false);
    setResimulating(true);
    try {
      const response = await simularCredito({
        valor_centavos: simulacao.valor_solicitado_centavos,
        prazo_meses: simulacao.prazo_meses,
        mova_product_id: MOVA_PRODUCT_ID,
        anniversary_day: day,
      });

      const newOptions = response.mova.simulacao.data.installments_options;
      const matchingOption =
        newOptions.find(
          opt => opt.installments_number === currentOption?.installments_number,
        ) || newOptions[0];

      if (matchingOption) {
        setCurrentOption(matchingOption);
        setCurrentInstallmentsOptionId(matchingOption.installments_option_id);
        setCurrentOperacaoId(response.operacao_id);
      }
    } catch (error) {
      Alert.alert(
        'Erro',
        'Não foi possível alterar a data de vencimento. Tente novamente.',
      );
    } finally {
      setResimulating(false);
    }
  };

 const handleEnviarProposta = () => {
  analytics().logEvent('emprestimo_clicou_enviar_proposta');
  setShowPhoneModal(true);
};
const handleConfirmarEnvio = async () => {
  setShowPhoneModal(false);
  setLoading(true);
  try {
    await enviarPropostaCredito({
      operacao_id: currentOperacaoId,
      installments_option_id: currentInstallmentsOptionId,
      sync: true,
    });
    analytics().logEvent('emprestimo_proposta_enviada_sucesso');
    (navigation as any).navigate('Home', {screen: 'Emprestimo'});
  } catch (error: any) {
      const body = error?.response?.data;
      const errorMessage: string | undefined =
        body?.error || body?.msg || body?.message || undefined;
      navigation.navigate('ConfirmationScreen', {
        title: 'Ocorreu um problema',
        text:
          errorMessage ||
          'Ocorreu um erro ao enviar sua proposta. Por favor, tente novamente mais tarde.',
        buttonAction: () => navigation.goBack(),
        closeIconAction: () => navigation.navigate('Initial'),
        buttonText: 'Tentar novamente',
        type: 'error',
        showSupport: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Alert.alert(
      'Cancelar proposta',
      'Tem certeza que deseja cancelar? Todo o progresso será perdido.',
      [
        {text: 'Não', style: 'cancel'},
        {
          text: 'Sim, cancelar',
          style: 'destructive',
          onPress: () => {
            (navigation as any).popToTop();
          },
        },
      ],
    );
  };

  return (
    <Container>
      <EmprestimoEligibilityGate />
      <Content>
        <Banner>
          <SafeAreaView style={{alignItems: 'center'}}>
            <CloseButton activeOpacity={0.7} onPress={handleCancel}>
              <CloseIcon name="x" />
            </CloseButton>
            <IconWrapper>
              <IconCheck name="check-circle" />
            </IconWrapper>
            <BannerTitle>Documentos enviados!</BannerTitle>
            <BannerSubtitle>
              Todos os documentos foram enviados com sucesso. Confira os dados
              abaixo e envie sua proposta de crédito.
            </BannerSubtitle>
          </SafeAreaView>
        </Banner>

        <SummaryCard>
          {resimulating && (
            <ActivityIndicator size="small" style={{marginBottom: 12}} />
          )}
          <SummaryRow>
            <SummaryLabel>Valor solicitado</SummaryLabel>
            <SummaryValue>
              {formatValor(currentOption?.operation_net_amount || 0)}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Valor financiado</SummaryLabel>
            <SummaryValue>
              {formatValor(currentOption?.operation_gross_amount || 0)}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Impostos (IOF)</SummaryLabel>
            <SummaryValue>
              {formatValor(currentOption?.installments_option_complement_info?.tax_iof_total_value || 0)}
            </SummaryValue>
          </SummaryRow>

          <Divider />
          <SummaryRow>
            <SummaryLabel>Você irá pagar</SummaryLabel>
            <SummaryValue>
              {currentOption?.installments_number}x de{' '}
              {formatValor(currentOption?.installment_amount || 0)} fixas
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>1º vencimento</SummaryLabel>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: RFValue(6)}}>
              <SummaryValue>
                {currentOption?.first_installment_date
                  ? formatDate(currentOption.first_installment_date)
                  : '-'}
              </SummaryValue>
            </View>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Taxa mensal</SummaryLabel>
            <SummaryValue>
              {formatRate(currentOption?.interest_monthly_rate || 0)}
            </SummaryValue>
          </SummaryRow>
        </SummaryCard>

        <StepsContainer>
          <StepsTitle>Etapas concluídas</StepsTitle>
          <StepRow>
            <StepIcon name="check-circle" />
            <StepText>Simulação realizada</StepText>
          </StepRow>
          <StepRow>
            <StepIcon name="check-circle" />
            <StepText>Endereço cadastrado</StepText>
          </StepRow>
          <StepRow>
            <StepIcon name="check-circle" />
            <StepText>Dados bancários informados</StepText>
          </StepRow>
          <StepRow>
            <StepIcon name="check-circle" />
            <StepText>Documentos enviados</StepText>
          </StepRow>
        </StepsContainer>
      </Content>

      <Footer>
        <SafeAreaView edges={['bottom']}>
          <DisclaimerText>
            A taxa de juros aplicada foi de{' '}
            {formatRate(currentOption?.interest_monthly_rate || 0)} ao mês e a CET
            (Custo Efetivo Total) ficou em{' '}
            {formatRate(currentOption?.gross_costs_annual_rate || 0)} ao ano.
          </DisclaimerText>
          {loading ? (
            <ActivityIndicator size="large" color={theme.colors.title} />
          ) : (
            <Button title="Enviar proposta" onPress={handleEnviarProposta} enabled={!loading} />
          )}
        </SafeAreaView>
      </Footer>

      <Modal
        visible={showDateModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDateModal(false)}>
        <TouchableWithoutFeedback onPress={() => setShowDateModal(false)}>
          <ModalOverlay>
            <TouchableWithoutFeedback>
              <ModalCard>
                <ModalTitle>Selecione o dia do vencimento</ModalTitle>
                <DayOptionsRow>
                  <DayPill
                    key={2}
                    activeOpacity={0.7}
                    onPress={() => handleSelectDay(10)}>
                    <DayPillText>Dia {10}</DayPillText>
                  </DayPill>
                </DayOptionsRow>
              </ModalCard>
            </TouchableWithoutFeedback>
          </ModalOverlay>
        </TouchableWithoutFeedback>
      </Modal>

    <Modal
        visible={showPhoneModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPhoneModal(false)}>
        <TouchableWithoutFeedback onPress={() => setShowPhoneModal(false)}>
          <PhoneModalOverlay>
            <TouchableWithoutFeedback>
              <PhoneModalCard>
                <PhoneModalIconWrapper>
                  <PhoneModalIcon name="phone-in-talk" />
                </PhoneModalIconWrapper>
                <PhoneModalTitle>Confirmação por telefone</PhoneModalTitle>
                <PhoneModalBody>
                  Podemos entrar em contato pelo número abaixo via ligação ou WhatsApp para confirmar seus dados.
                </PhoneModalBody>
                {MOSTRAR_DOIS_NUMEROS && numeroSuporte1 ? (
                  <PhoneModalPhoneRow>
                    <PhoneModalWhatsIcon name="whatsapp" />
                    <PhoneModalPhoneNumber>{numeroSuporte1}</PhoneModalPhoneNumber>
                  </PhoneModalPhoneRow>
                ) : null}
                {MOSTRAR_DOIS_NUMEROS && numeroSuporte1 && numeroSuporte2 ? (
                  <PhoneModalDivider>Ou</PhoneModalDivider>
                ) : null}
                {numeroSuporte2 ? (
                  <PhoneModalPhoneRow>
                    <PhoneModalWhatsIcon name="whatsapp" />
                    <PhoneModalPhoneNumber>{numeroSuporte2}</PhoneModalPhoneNumber>
                  </PhoneModalPhoneRow>
                ) : null}
                <PhoneModalButtonWrapper>
                  <Button title="Ok, entendi" onPress={handleConfirmarEnvio} enabled={!loading} />
                </PhoneModalButtonWrapper>
              </PhoneModalCard>
            </TouchableWithoutFeedback>
          </PhoneModalOverlay>
        </TouchableWithoutFeedback>
      </Modal>
    </Container>
  );
}